import { AdditionalServiceDetailsTab } from '@components';
import { useTenantVat, useUserVehicle } from '@hooks';
import { IDate } from '@models';
import { DispachedServicePart, InspectionCategory, Order, Vehicle } from '@services';
import { useStore } from '@stores';
import { formatDateWithOffset, Utils } from '@utils';
import { Button, Col, Form, Modal, Row, Typography } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useMemo, useState } from 'react';

const { Text } = Typography;

declare const abp: any;

interface IProps {
  open: boolean;
  order: Order;
  vehicle: Vehicle;
  setOpen: (open: boolean) => void;
}

export const AdditionOrderModalForm: React.FC<IProps> = observer(
  ({ open, order, vehicle, setOpen }) => {
    const { orderStore, serviceStore } = useStore();
    const { calendarLoading } = orderStore;
    const { additionalServiceParts, additionalSelectedServices } = serviceStore;
    const [form] = Form.useForm();
    const [dropOff, setDropOff] = useState<IDate>({ startAt: undefined });
    const [selectedInspections, setSelectedInspections] = useState<InspectionCategory[]>([]);

    // very fast workaround, pass this from the previous screen on refactor
    const userVehicle = useUserVehicle(order.customer.userId, vehicle.id);

    const vat = useTenantVat();

    const onSubmit = useCallback(async () => {
      const { dropOffDate, whileWait, approval } = form.getFieldsValue();
      await form.validateFields();

      const createRequestOrder: any = {
        title: additionalSelectedServices.map((service) => service.name).join(', '),
        dropOffDate,
        vehicleOwner: !!userVehicle,
        relationId: order.relationId,
        registrationNumber: order.registrationNumber,
        vin: order.vin,
        status: approval ? 'Approval' : 'Todo',
        price: totalPrice,
        duration: additionalSelectedServices.reduce(
          (totalDuration, service) =>
            totalDuration + (service.duration === 0 ? 1 : service.duration),
          0
        ),
        whileWait,
        makeAndModel: `${vehicle?.combinedMake ?? ''} ${vehicle?.combinedModel ?? ''}`.trim(),
        customerId: order.customerId,
        tenantId: abp.session.tenantId,
        inspectionCategoryIds: selectedInspections.map((inspection) => inspection.id),
        serviceOrders: additionalSelectedServices.map((service) => {
          return {
            title: service.name,
            price: service.price,
            duration: service.duration === 0 ? 1 : service.duration,
            isFixed: service.isFixed,
            vat: service.isVat ? vat : 0,
            discount: service.discount,
            serviceId: service.id,
            extras:
              additionalServiceParts
                .get(service.id!)
                ?.map((part: DispachedServicePart) => {
                  return {
                    vat,
                    name: part.name,
                    number: part.number,
                    reference: part.reference,
                    quantity: part.quantity,
                    price: part.price,
                    type: part.type,
                    costPrice: part.costPrice,
                    margin: part.margin,
                    discount: part.discount,
                    inventoryId: part.inventoryId,
                  };
                })
                .filter((x) => x.name) || [],
          };
        }),
      };

      await orderStore.create(createRequestOrder).then(() => {
        setOpen(false);
        serviceStore.clearAdditionalSelectedServices();
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      order,
      vehicle,
      additionalSelectedServices,
      additionalServiceParts,
      form,
      vat,
      userVehicle,
      selectedInspections,
    ]);

    const setDropOffDate = (value: IDate) => {
      setDropOff({ startAt: formatDateWithOffset(value.startAt) });
      form.setFieldsValue({ dropOffDate: formatDateWithOffset(value.startAt) });
    };

    const onCancel = () => {
      setOpen(false);
      form.resetFields();
      setDropOff({ startAt: undefined });
      serviceStore.clearAdditionalSelectedServices();
    };

    const labourPrice = useMemo(
      () =>
        additionalSelectedServices.reduce((acc, curr) => acc + Utils.calculateLabourPrice({ ...curr }), 0),
      [additionalSelectedServices]
    );

    const partsPrice = Utils.calculatePartsPrice(
      Array.from(additionalServiceParts.values()).flat()
    );

    const subTotal = labourPrice + partsPrice;

    const serviceVat = additionalSelectedServices.reduce(
      (acc, curr) =>
        acc + (curr.isVat ? Utils.calculateVat({ ...curr, vat: curr.isVat ? vat : 0 }) : 0),
      0
    );

    const partsVat = Array.from(additionalServiceParts.values())
      .flat()
      .reduce(
        (acc, curr) => acc + Utils.calculateVat({ ...curr, duration: curr.quantity, vat }),
        0
      );

    const vatAmount = serviceVat + partsVat;

    const totalPrice = subTotal + vatAmount;

    return (
      <Modal
        title="Add job card"
        open={open}
        width={1200}
        onCancel={() => setOpen(false)}
        footer={
          <>
            <br />
            <Row justify="space-between" align="middle">
              <Col>
                <Row gutter={16}>
                  <Col>
                    <Text>Labour: {Utils.formatCurrency(labourPrice)}</Text>
                  </Col>
                  <Col>
                    <Text>Parts & Fluids: {Utils.formatCurrency(partsPrice)}</Text>
                  </Col>
                  <Col>
                    <Text>Sub Total: {Utils.formatCurrency(subTotal)}</Text>
                  </Col>
                  <Col>
                    <Text>VAT: {Utils.formatCurrency(vatAmount)}</Text>
                  </Col>
                </Row>
              </Col>
              <Col>
                <Row gutter={8}>
                  <Col
                    style={{ backgroundColor: '#E3F6FF', padding: '4px 14px', borderRadius: '4px' }}
                  >
                    <Text strong>TOTAL: {Utils.formatCurrency(totalPrice)}</Text>
                  </Col>
                  <Col>
                    <Button type="default" onClick={onCancel}>
                      Cancel
                    </Button>
                  </Col>
                  <Col>
                    <Button
                      type="primary"
                      disabled={
                        !additionalSelectedServices.length || calendarLoading || !form.getFieldValue('dropOffDate')
                      }
                      loading={calendarLoading}
                      onClick={onSubmit}
                    >
                      Confirm booking
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </>
        }
      >
        <AdditionalServiceDetailsTab
          requestForm={form}
          dropOffDate={dropOff}
          selectedInspections={selectedInspections}
          setDropOffDate={setDropOffDate}
          setSelectedInspections={setSelectedInspections}
        />
      </Modal>
    );
  }
);
